/* eslint-disable no-mixed-operators */
// /* eslint-disable jsx-a11y/anchor-is-valid */
import {FC} from 'react'
import {useIntl} from 'react-intl'
import {Link, useNavigate} from 'react-router-dom'
import {PreviewCard} from '../../../../common/component/PreviewCard'
import { SettingOption } from '../../../../common/component/DropdownMenu'
import { CustomInputRenderer } from '../../../../common/component/CustomInputRenderer'
import { SubSectionHeader } from '../../../../common/component/SubSectionHeader'
import { parseLocalDate } from '../../../../util/Locale'
import { CriticalityLevel, IATicket } from '../Ticket.model'
import { ClientEmbedered } from '../../../client/components/ClientEmbedered'
import { AssetEmbedered } from '../../../asset/components/AssetEmbedered'
import { ContactEmbedered } from '../../../contact/components/ContactEmbedered'
import classNames from 'classnames'

interface Props {
  data?: IATicket
  loading: boolean
  setRowSelected: (rowId?: string) => void
  setToastEntry: ([MessageType, string]) => void
}

export const TicketPreview: FC<Props> = ({data, loading, setRowSelected, setToastEntry}: Props) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const settingOptions: SettingOption[] = [
    {
      label: intl.formatMessage({id: 'UTIL.DETAILS'}),
      icon: 'bi-book',
      action: () => navigate(`/tickets/${data?.ticket?.id}`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.EDIT'}),
      icon: 'bi-pencil',
      action: () => navigate(`/tickets/${data?.ticket?.id}/edit`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.CREATE.TASK'}),
      icon: 'bi-stickies',
      action: () => navigate(`/tickets/${data?.ticket?.id}/tasks/new`),
    },
  ]

  return (
    <PreviewCard
      loading={loading}
      title={data && data.ticket ? `${data.ticket.title}` : ''}
      subTitle={data?.ticket?.sequence}
      pathOnClickHandler={`/tickets/${data?.ticket?.id}`}
      closeOnClickHandler={() => setRowSelected(undefined)}
      settingOptions={settingOptions}
      content={
        data &&
        data.ticket && (
          <div>
            <div className='row mt-10'>
              <div className='px-5'>
                <div className='row mb-5'>
                  <ClientEmbedered client={data.ticket.client} />
                  {data.ticket.asset && <AssetEmbedered asset={data.ticket.asset} />}
                </div>
              </div>
            </div>

            <div className='row'>
              <SubSectionHeader
                separator={false}
                title={intl.formatMessage({id: 'UTIL.GENERAL'})}
              />

              <div className='px-5'>
                <div className='row mb-5'>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                    value={parseLocalDate(
                      data.ticket.updatedAt,
                      intl.formatMessage({id: 'I18N.LOCALE'})
                    )}
                  ></CustomInputRenderer>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ID'})}
                    value={`${data.ticket.sequence}`}
                  ></CustomInputRenderer>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'TICKET.TITLE'})}
                    object={
                      <span className='fs-6 ms-3 text-dark'>
                        <Link className='' to={`/tickets/${data.ticket.id}`}>
                          {data.ticket.title}
                        </Link>
                      </span>
                    }
                  ></CustomInputRenderer>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.STATUS'})}
                    value={intl.formatMessage({
                      id: `DROPDOWN.TICKET.STATUS.${data.ticket.statusCode}`,
                    })}
                  ></CustomInputRenderer>
                  <div className='row mb-5'>
                    <label className='col-xxl-3 text-gray-700 fs-6'>
                      {intl.formatMessage({id: 'UTIL.CRITICALITY_LEVEL'})}
                    </label>
                    <div
                      className={classNames('col-xxl-9 ps-6 fw-bold fs-6', {
                        'text-danger': data.ticket.criticalityCode === CriticalityLevel.SEVERE,
                        'text-warning': data.ticket.criticalityCode === CriticalityLevel.HIGH,
                        'text-success': data.ticket.criticalityCode === CriticalityLevel.MODERATE,
                        'text-primary': data.ticket.criticalityCode === CriticalityLevel.LOW,
                      })}
                    >
                      {intl.formatMessage({
                        id: `TICKET.CRITICALITY.${data.ticket.criticalityCode}`,
                      })}
                    </div>
                  </div>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.REPORTED_DATE_TIME'})}
                    value={parseLocalDate(data.ticket.reportedAt, '', '', {
                      hours: true,
                      minutes: true,
                      seconds: false,
                      millis: false,
                    })}
                  ></CustomInputRenderer>{' '}
                  <ContactEmbedered
                    label={intl.formatMessage({id: 'UTIL.REPORTED_BY'})}
                    contact={data.ticket.reportedBy}
                  />
                </div>
              </div>

              {(data.ticket.countryCode || data.ticket.address) && (
                <>
                  <SubSectionHeader
                    title={intl.formatMessage({id: 'UTIL.LOCATION'})}
                    separator={false}
                  />
                  <div className='px-5'>
                    <div className='row mb-5'>
                      {data.ticket.countryCode && (
                        <CustomInputRenderer
                          label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                          value={intl.formatMessage({
                            id: `DROPDOWN.COUNTRY.${data.ticket.countryCode}`,
                          })}
                        ></CustomInputRenderer>
                      )}

                      {data.ticket.address && (
                        <CustomInputRenderer
                          label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                          value={data.ticket.address}
                        ></CustomInputRenderer>
                      )}
                    </div>
                  </div>
                </>
              )}

              <SubSectionHeader
                separator={false}
                title={intl.formatMessage({id: 'UTIL.METRICS'})}
              />

              <div className='px-5'>
                <div className='row mb-5'>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'TICKET.TASKS'})}
                    value={`${data?.ticket.totalTasks || 0}`}
                  ></CustomInputRenderer>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ATTACHMENTS'})}
                    value={`${data?.ticket.totalAttachments || 0}`}
                  ></CustomInputRenderer>
                </div>
              </div>
            </div>
          </div>
        )
      }
    ></PreviewCard>
  )
}
